import type { ThreadApiMessage } from '@teo/widget-shared';
import {
  getWidgetSession,
  updateWidgetSessionThreadId,
} from '../session/widget-session.js';
import type { WidgetMessageEventPayload, WidgetRealtimeCallbacks } from './types.js';
import { createDebouncedThreadRefetch } from './debounced-thread-refetch.js';
import { logRealtime } from './realtime-logger.js';

const THREAD_UPDATED_EVENT = 'teo-widget-thread-updated';
const MESSAGE_RECEIVED_EVENT = 'teo-widget-message-received';

function dispatchThreadUpdated(
  threadId: string,
  prevThreadId: string | null,
  message: ThreadApiMessage,
): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(THREAD_UPDATED_EVENT, {
      detail: {
        threadId,
        threadIdNum: Number(threadId),
        prevThreadId,
        message,
      },
    }),
  );
}

function dispatchMessageReceived(
  threadId: string,
  message: ThreadApiMessage,
  threadIdChanged: boolean,
): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(MESSAGE_RECEIVED_EVENT, {
      detail: { threadId, message, threadIdChanged },
    }),
  );
}

function syncStorageThreadId(threadId: string, prevThreadId: string | null): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem('thread_id', threadId);
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'thread_id',
        newValue: threadId,
        oldValue: prevThreadId,
        storageArea: localStorage,
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function createWidgetMessageEventHandler(
  callbacks: WidgetRealtimeCallbacks,
): {
  handle: (event: WidgetMessageEventPayload) => void;
  handleThreadSignal: (threadId: string) => void;
  cancelPendingRefetch: () => void;
} {
  const { schedule, cancel } = createDebouncedThreadRefetch(
    async (threadId, threadIdChanged) => {
      logRealtime('refetch', 'debounced refetch starting', {
        threadId,
        threadIdChanged,
      });
      try {
        await callbacks.onThreadMessages(threadId, { threadIdChanged });
        logRealtime('refetch', 'debounced refetch completed', { threadId });
      } catch (error) {
        logRealtime('refetch', 'debounced refetch failed', { threadId, error }, 'error');
        throw error;
      }
    },
  );

  const handle = (event: WidgetMessageEventPayload) => {
    if (!event?.message) {
      logRealtime('handler', 'skipped: no message in payload', event, 'warn');
      return;
    }
    if (!event.message.thread_id) {
      logRealtime('handler', 'skipped: no thread_id', event.message, 'warn');
      return;
    }

    const threadId = String(event.message.thread_id);
    const session = getWidgetSession();
    const prevThreadId =
      session?.threadId != null ? String(session.threadId) : null;
    const threadIdChanged = prevThreadId !== threadId;

    logRealtime('handler', 'NewSiteWidgetMessageEvent accepted', {
      threadId,
      prevThreadId,
      threadIdChanged,
      messageId: event.message.id,
      messageRole: event.message.message_role,
      sourceId: event.message.source_id,
      type: event.message.type,
      rawType: event.message.raw_message?.type,
      textPreview: (event.message.message || event.message.raw_message?.payload || '').slice(0, 120),
      createdAt: event.message.created_at,
    });

    if (threadIdChanged) {
      const numericId = Number(threadId);
      if (Number.isFinite(numericId) && numericId > 0) {
        updateWidgetSessionThreadId(numericId);
      }
      syncStorageThreadId(threadId, prevThreadId);
      dispatchThreadUpdated(threadId, prevThreadId, event.message);
      callbacks.onThreadIdUpdated?.(threadId, prevThreadId, event.message);
    }

    dispatchMessageReceived(threadId, event.message, threadIdChanged);
    schedule(threadId, threadIdChanged);
    logRealtime('handler', 'refetch scheduled', { threadId, threadIdChanged });
  };

  const handleThreadSignal = (threadId: string): void => {
    const session = getWidgetSession();
    const prevThreadId =
      session?.threadId != null ? String(session.threadId) : null;
    const threadIdChanged = prevThreadId !== threadId;

    logRealtime('handler', 'thread signal accepted (refetch)', {
      threadId,
      prevThreadId,
      threadIdChanged,
    });

    if (threadIdChanged) {
      const numericId = Number(threadId);
      if (Number.isFinite(numericId) && numericId > 0) {
        updateWidgetSessionThreadId(numericId);
      }
      syncStorageThreadId(threadId, prevThreadId);
      callbacks.onThreadIdUpdated?.(threadId, prevThreadId);
    }

    schedule(threadId, threadIdChanged);
    logRealtime('handler', 'refetch scheduled (signal)', {
      threadId,
      threadIdChanged,
    });
  };

  return { handle, handleThreadSignal, cancelPendingRefetch: cancel };
}
